import { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { ENDPOINTS } from "@/api/endpoints";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Heart, Mail, Shield } from "lucide-react";

export default function Login() {
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [codeSent, setCodeSent] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Handle data passed from patient signup or unauthorized access
  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
    if (location.state?.message) {
      setSuccess(location.state.message);
    }

    // Check for unauthorized access redirect
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.get("reason") === "unauthorized") {
      setError(
        "Access denied. You were logged out for trying to access an unauthorized page."
      );
    }
  }, [location.state, location.search]);

  const handleSendCode = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      await apiClient.post("/api/auth/email/send-code", { email });
      setCodeSent(true);
      setSuccess("Verification code sent to your email!");
    } catch (err) {
      if (err.data?.code === "SIGNUP_REQUIRED") {
        setError(
          <span>
            Please complete patient signup first.{" "}
            <Link
              to="/patient-signup"
              className="underline text-blue-600 hover:text-blue-500"
            >
              Sign up here
            </Link>
          </span>
        );
      } else {
        setError(err.message || "Failed to send verification code");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await apiClient.post("/api/auth/email/verify", { email, code });

      // Invalidate current user query to trigger refetch and wait for it
      await queryClient.invalidateQueries(["currentUser"]);
      await queryClient.refetchQueries(["currentUser"]);

      // Redirect to dashboard
      navigate("/");
    } catch (err) {
      setError(err.data?.error || "Verification failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // Redirect to Google OAuth endpoint using ApiClient base URL
    window.location.href = `${apiClient.baseURL}${ENDPOINTS.AUTH.GOOGLE}`;
  };

  const handleBackToEmail = () => {
    setCodeSent(false);
    setCode("");
    setError("");
    setSuccess("");
    setEmail("");
  };

  const handleResendCode = async () => {
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      await apiClient.post("/api/auth/email/send-code", { email });
      setSuccess("New verification code sent to your email!");
      setCode(""); // Clear the code input
    } catch (err) {
      setError(err.message || "Failed to resend verification code");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <Heart className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            ClubMed
          </CardTitle>
          <p className="text-slate-600">
            {codeSent ? "Enter verification code" : "Sign in to your account"}
          </p>
        </CardHeader>
        <CardContent>
          {!codeSent ? (
            // Email input form
            <form onSubmit={handleSendCode} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <AlertDescription className="text-green-800">
                    {success}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="email"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <Mail className="w-4 h-4" />
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  data-testid="login-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
                <p className="text-xs text-slate-500">
                  We&apos;ll send you a verification code to sign in
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-send-code-btn">
                {isLoading ? "Sending Code..." : "Send Verification Code"}
              </Button>
            </form>
          ) : (
            // Verification code form
            <form onSubmit={handleVerifyCode} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <AlertDescription className="text-green-800">
                    {success}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="code"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Verification Code
                </label>
                <Input
                  id="code"
                  type="text"
                  data-testid="login-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Enter 6-character code"
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                  required
                />
                <p className="text-xs text-slate-500">
                  Check your email for the verification code sent to{" "}
                  <span className="font-medium">{email}</span>
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading} data-testid="login-verify-btn">
                {isLoading ? "Verifying..." : "Verify & Sign In"}
              </Button>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleResendCode}
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Resend Code"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleBackToEmail}
                  disabled={isLoading}
                >
                  Use Different Email
                </Button>
              </div>
            </form>
          )}

          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-slate-500">
                  Or continue with
                </span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              className="w-full mt-4"
              onClick={handleGoogleLogin}
            >
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path
                  fill="currentColor"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="currentColor"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="currentColor"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="currentColor"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              Continue with Google
            </Button>
          </div>

          {!codeSent && (
            <div className="mt-6 text-center space-y-3">
              <p className="text-sm text-slate-600">
                New patient?{" "}
                <Link
                  to="/patient-signup"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Create your account first
                </Link>
              </p>

              <div className="pt-3 border-t border-slate-200">
                <p className="text-sm text-slate-600">
                  Are you a healthcare provider?{" "}
                  <Link
                    to="/practice-owner-signup"
                    className="font-medium text-blue-600 hover:text-blue-500"
                  >
                    Register your practice
                  </Link>
                </p>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
